import { useState } from 'react';
import { PARTICIPANT_DEFAULT_FILTERS, PARTICIPANT_SEARCH_FIELDS } from '../../constants/searchConfigs';
import { SearchField } from '../../models/serviceModels/SearchConfig';
import { DateRange } from '../UI/DateRangeInput';
import DateRangeInput from '../UI/DateRangeInput';
import Input from '../UI/Input';
import Select from '../UI/Select';
import Checkbox from '../UI/Checkbox';
import Button from '../UI/Button';
import InputWithFloatingButtons from '../UI/InputWithFloatingButtons';

export interface ParticipantFilter {
    query: string;
    fields: { fieldId: string; value: any }[];
    excludeInactive: boolean;
    assignedToMe: boolean;
}

interface FieldSearchPanelProps {
    searching: boolean;
    hasSearched: boolean;
    programOptions: { text: string; value: string }[];
    onSearch: (filter: ParticipantFilter) => void;
}

const emptyValues = (): Record<string, any> =>
    Object.fromEntries(PARTICIPANT_SEARCH_FIELDS.map(f => [f.fieldId, f.type === 'dateRange' ? { start: '', end: '' } : '']));

const filterTokenPattern = /"([^":]+):([^"]*)"/g;

const getFilterToken = (field: SearchField, value: any) => {
    if (field.type === 'dateRange') {
        const range = value as DateRange;
        return range?.start || range?.end ? `"${field.label}:${range.start || ''}..${range.end || ''}"` : '';
    }

    return value ? `"${field.label}:${value}"` : '';
};

const stripFilterTokens = (query: string) => query.replace(filterTokenPattern, (token, label) =>
    PARTICIPANT_SEARCH_FIELDS.some(field => field.label === label) ||
    PARTICIPANT_DEFAULT_FILTERS.some(filter => filter.disableText === label)
        ? ''
        : token
).replace(/\s{2,}/g, ' ').trim();

export const buildSearchQuery = (
    query: string,
    values: Record<string, any>,
    flags: Record<string, boolean>
) => {
    const tokens = [
        ...PARTICIPANT_SEARCH_FIELDS.map(field => getFilterToken(field, values[field.fieldId])).filter(Boolean),
        ...PARTICIPANT_DEFAULT_FILTERS
            .filter(filter => flags[filter.flagKey])
            .map(filter => `"${filter.disableText}:True"`)
    ];

    return [...tokens, stripFilterTokens(query)].filter(Boolean).join(' ');
};

const parseSearchQuery = (query: string) => {
    const values = emptyValues();
    const flags: Record<string, boolean> = {};
    let match: RegExpExecArray | null;

    filterTokenPattern.lastIndex = 0;
    while ((match = filterTokenPattern.exec(query))) {
        const [, label, value] = match;
        const field = PARTICIPANT_SEARCH_FIELDS.find(candidate => candidate.label === label);
        if (field) {
            if (field.type === 'dateRange') {
                const [start = '', end = ''] = value.split('..');
                values[field.fieldId] = { start, end };
            } else {
                values[field.fieldId] = value;
            }
            continue;
        }

        const filter = PARTICIPANT_DEFAULT_FILTERS.find(candidate => candidate.disableText === label);
        if (filter) flags[filter.flagKey] = value.toLowerCase() === 'true';
    }

    return { values, flags };
};

export const buildFilter = (query: string, values: Record<string, any>, flags: Record<string, boolean>): ParticipantFilter => {
    const fields = PARTICIPANT_SEARCH_FIELDS
        .filter(f => {
            const v = values[f.fieldId];
            if (f.type === 'dateRange') {
                const range = v as DateRange | undefined;
                return !!(range?.start || range?.end);
            }
            return !!v;
        })
        .map(f => ({ fieldId: f.fieldId, value: values[f.fieldId] }));

    return {
        query,
        fields,
        excludeInactive: !(flags['excludeInactive'] ?? false),
        assignedToMe: flags['assignedToMe'] ?? false,
    };
};

const FieldSearchPanel = ({ searching, hasSearched, programOptions, onSearch }: FieldSearchPanelProps) => {
    const [query, setQuery] = useState('');
    const [values, setValues] = useState<Record<string, any>>(emptyValues());
    const [flags, setFlags] = useState<Record<string, boolean>>({});
    const [showFilters, setShowFilters] = useState(false);
    const updateQueryFromFilters = (nextValues: Record<string, any>, nextFlags: Record<string, boolean>) =>
        setQuery(buildSearchQuery(query, nextValues, nextFlags));

    const setValue = (fieldId: string, value: any) => {
        const nextValues = { ...values, [fieldId]: value };
        setValues(nextValues);
        updateQueryFromFilters(nextValues, flags);
    };

    const setFlag = (flagKey: string, checked: boolean) => {
        const nextFlags = { ...flags, [flagKey]: checked };
        setFlags(nextFlags);
        updateQueryFromFilters(values, nextFlags);
    };

    const handleQueryChange = (nextQuery: string) => {
        const parsed = parseSearchQuery(nextQuery);
        setValues(parsed.values);
        setFlags(parsed.flags);
        setQuery(nextQuery);
    };

    const getInput = (field: SearchField) => {
        const id = `psf-${field.fieldId}`;
        switch (field.type) {
            case 'text':
            case 'phone':
                return (
                    <Input
                        id={id}
                        label={field.label}
                        value={values[field.fieldId]}
                        disableAutocomplete={true}
                        onChange={v => setValue(field.fieldId, v)}
                    />
                );
            case 'date':
                return (
                    <Input
                        id={id}
                        label={field.label}
                        type="date"
                        value={values[field.fieldId]}
                        disableAutocomplete={true}
                        onChange={v => setValue(field.fieldId, v)}
                    />
                );
            case 'dateRange':
                return (
                    <DateRangeInput
                        id={id}
                        label={field.label}
                        value={values[field.fieldId] as DateRange}
                        onChange={v => setValue(field.fieldId, v)}
                    />
                );
            case 'program': {
                const selectedProgramId = programOptions.find(option => option.text === values[field.fieldId])?.value || '';
                return (
                    <Select
                        id={id}
                        label={field.label}
                        value={selectedProgramId}
                        portalMenu={true}
                        onChange={v => setValue(field.fieldId, programOptions.find(option => option.value === v)?.text || '')}
                    >
                        <option value="">Select One</option>
                        {programOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.text}</option>
                        ))}
                    </Select>
                );
            }
            default:
                return null;
        }
    };

    const handleSearch = (q = query, v = values, f = flags) => {
        onSearch(buildFilter(stripFilterTokens(q), v, f));
    };

    const handleReset = () => {
        const clearedValues = emptyValues();
        const clearedFlags: Record<string, boolean> = {};
        setQuery('');
        setValues(clearedValues);
        setFlags(clearedFlags);
        if (hasSearched) {
            handleSearch('', clearedValues, clearedFlags);
        }
    };

    const handleKeyUp = (key: string) => {
        if (key === 'Enter') handleSearch();
    };

    return (
        <div className="search-container">
            <div className="row margin-vertical-2 align-items-center">
                <div className="column">
                    <InputWithFloatingButtons
                        id="field-search-query"
                        value={query}
                        placeholder="Search by name, phone, or email..."
                        onChange={handleQueryChange}
                        onKeyUp={handleKeyUp}
                        buttons={[
                            { text: 'Reset', onClick: handleReset, className: 'secondary' },
                            { text: showFilters ? 'Hide Filters' : 'Show Filters', onClick: () => setShowFilters(s => !s), className: 'secondary' },
                        ]}
                    />
                </div>
                <div className="column-2">
                    <Button
                        id="participant-search-btn"
                        text="Search"
                        className="block action"
                        onClick={() => handleSearch()}
                        loading={searching}
                        disabled={searching}
                    />
                </div>
            </div>

            {showFilters && <div className="search-filters-container participant-search-filters">
                <div className="row padding-5">
                    {PARTICIPANT_SEARCH_FIELDS.map(f => (
                        <div className="column-auto margin-vertical-3" key={f.fieldId}>
                            <div className="border padding-3 rounded">
                                {getInput(f)}
                            </div>
                        </div>
                    ))}
                    {PARTICIPANT_DEFAULT_FILTERS.map((f, i) => (
                        <div className="column-auto margin-vertical-3" key={f.flagKey}>
                            <div className="border padding-3 rounded">
                                <Checkbox
                                    id={`psf-flag-${i}`}
                                    label={f.disableText}
                                    checked={flags[f.flagKey] ?? false}
                                    onChange={c => setFlag(f.flagKey, c)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>}
        </div>
    );
};

export default FieldSearchPanel;
